<template>
	<transition name="fade" appear>
		<Page class="experiance-builder" v-if="isShopColorsSet && allowedToRender">
			<Column no-margin>
				<PageHeader :title="title" :body="headerText.body" />
			</Column>

			<Column no-margin class="page-bg page-padding-top">
				<div class="experiance-builder__block" v-if="isShopReady && authRequirements">
					<ExperiencePrebuilder v-if="!allowedToEnterBuilder" />
					<ExperienceBuilder v-else />
				</div>

				<div class="experiance-builder__fake-space" v-else>
					<Spinner />
				</div>
			</Column>
		</Page>

		<Page class="addon-checker" v-else-if="isAddonShop">
			<Column no-margin>
				<PageHeader :title="title" :body="headerTextAddon.body" />
			</Column>

			<Column no-margin class="page-bg page-padding-top">
				<div class="experiance-builder__fake-space">
					<Spinner />
				</div>
			</Column>
		</Page>
	</transition>
</template>

<script>
import ExperiencePrebuilder from '@/components/experiencebuilder/ExperiencePrebuilder';
import ExperienceBuilder from '@/components/experiencebuilder/ExperienceBuilder';
import PageHeader from '@/components/ui/PageHeader';

import ShopAuthenticationHandler from '@/mixins/ShopAuthenticationHandler.js';
import ShopCartPrefillHandler from '@/mixins/ShopCartPrefillHandler';

import AddMetaObjectToProducts from '@/plugins/AddMetaObjectToProducts';
import FormatShopData from '@/plugins/FormatShopData';
import SetBasicShopData from '@/plugins/SetBasicShopData';
import { createClient } from '@/plugins/ContentfulClient';
import SetMetaDataMixin from '@/mixins/SetMetaDataMixin';
import SetCssVars from '@/mixins/SetCssVars';
import preserveUTMParams from '@/utils/preserveUTMParams';
import capitalizeWords from '@/utils/capitalizeWords';

export default {
	name: 'PageSlug',

	components: {
		ExperiencePrebuilder,
		ExperienceBuilder,
		PageHeader
	},

	mixins: [ShopAuthenticationHandler, ShopCartPrefillHandler, SetMetaDataMixin, SetCssVars],

	/* Get the status of the shop request shopData from contentfull or json files
	 * MEMBERSALE
	 * TRAVEL ONLY
	 * PREREGISTRATION
	 *
	 * FLOW:
	 *
	 * Get the status of the shop,
	 * handle paylogic redirects and queue token
	 * if all valid get the shop products
	 *
	 * More comments below
	 *
	 */

	async asyncData({ req, isServer, app, store, params, route, redirect }) {
		const client = createClient();
		const [config] = await Promise.all([
			client.getEntries({
				content_type: 'ossConfig',
				include: 4
			})
		]);

		const shopsData = FormatShopData(config);
		// console.log('shopsData:', shopsData);
		const shopData = shopsData.find(item => item.slug === route.params.slug);

		// If no shop slug is provided redirect to the index
		if (!shopData) {
			console.log('no shop slug provided, redirecting to index');
			redirect({ path: '/', query: route.query });
			return false;
		}

		if (!shopData.saleActive) {
			console.log('sale not active');
			redirect({ path: '/', query: route.query });
			return false;
		}

		// Don't show tickets option in the shop if there's an upgrade offer (at least for Defqon, this might change in the future. You never know)
		/* if (shopData.festivalData.title.includes('upgrade')) {
			shopData.salesOptions = shopData.salesOptions.filter(item => item.option !== 'ticket');
		} */

		if (route.query.shopToken) {
			shopData.shopToken = route.query.shopToken;
		}

		if (store.getters['shopconfig/getCart'] === null) {
			store.commit('shopconfig/setData', shopData);
		}

		// Set layout style for tickets
		if (shopData.shopTheme.fields.ticketLayoutStyle) {
			store.commit('ui/setTicketLayoutStyle', shopData.shopTheme.fields.ticketLayoutStyle);
		}

		// Set card amount per row
		if (shopData.shopTheme.fields?.cardsPerRow) {
			store.commit('ui/setCardsPerRow', shopData.shopTheme.fields.cardsPerRow);
		}

		//Add meta object to product data
		AddMetaObjectToProducts(shopData, store);

		//Set the general shop data
		const ossConfig = config.items[0].fields;
		SetBasicShopData(ossConfig, store);

		// Override the locale
		if (shopData.language) store.commit('setLocale', shopData.language);

		// Override the translations
		if (shopData.customTranslations) store.commit('shopconfig/setCustomTranslations', shopData.customTranslations);

		let navigationFlow = [];
		const useSalesOptions = store.getters['shopconfig/getUseSalesOptions'];

		shopData.salesOptions.forEach(item => {
			const option = item.option === 'ticket' ? 'tickets' : item.option;
			const shop = route.params.slug;
			let routerObject;
			// If we are using the legacy flow we only allow one sales option per type
			if (useSalesOptions) {
				// use different format for tickets and extras
				if (option === 'tickets' || option === 'extras') {
					routerObject = {
						title: item.title,
						name: `slug-${option}-${item.slug}`,
						route: `/${shop}/${option}/${item.slug}`,
						path: `/${shop}/${option}/${item.slug}`,
						type: item.option,
						current: false,
						completed: false
					};
				} else {
					routerObject = {
						title: item.title,
						name: `slug-${option}`,
						route: `/${shop}/${option}`,
						path: `/${shop}/${option}`,
						type: item.option,
						current: false,
						completed: false
					};
				}
			} else {
				routerObject = {
					title: capitalizeWords(item.option),
					name: `slug-${option}`,
					route: `/${shop}/${option}`,
					path: `/${shop}/${option}`,
					type: item.option,
					current: false,
					completed: false
				};

				if (navigationFlow.some(route => route.type === item.option)) return;
			}
			// Tickets step shouldn't be shown when an upgrade is available
			if (!store.getters['shopconfig/getHasUpgradeModule'] || routerObject.route !== 'tickets') {
				navigationFlow.push(routerObject);
			}
		});

		if (store.getters['shopapi/getMappedProductsInBasket'].membership) {
			navigationFlow = navigationFlow.filter(option => option.route !== 'tickets');
		}

		store.commit('setShopRoutingFlow', navigationFlow);

		return {
			data: shopData,
			shopsData
		};
	},

	data() {
		return {
			isShopColorsSet: false,
			isShopReady: false,
			authRequirements: false,
			allowedToRender: false,
			forceBackToFlow: true,
			orderReference: null,
			headerText: {
				body: this.$t('page.shop.lead')
			},
			headerTextAddon: {
				body: this.$te('page.shop.lead_addon_shop')
					? this.$t('page.shop.lead_addon_shop')
					: this.$t('page.shop.lead')
			}
		};
	},

	computed: {
		title() {
			return this.$store.getters['shopconfig/getShopTitle'];
		},

		brand() {
			return this.$store.getters.getBrand;
		},

		isOnlyOneShopActive() {
			const activeShops = this.shopsData.filter(item => item.saleActive === true);
			const activeShopsInOverview = this.shopsData.filter(item => item.shopVisibleInOverview === true);

			return activeShops.length === 1 || activeShopsInOverview.length === 1;
		},

		useSalesOptions() {
			return this.$store.getters['shopconfig/getUseSalesOptions'];
		},

		shopCart() {
			return this.$store.getters['shopconfig/getCart'];
		},

		shopConfig() {
			return this.$store.getters['shopconfig/getData'];
		},

		title() {
			return this.shopConfig.title;
		},

		hasUpgradeModule() {
			return this.$store.getters['shopconfig/getHasUpgradeModule'];
		},

		isOrderIdRequired() {
			return this.shopConfig && this.shopConfig.orderIdRequired;
		},

		isAccessIdRequired() {
			return this.shopConfig && this.shopConfig.accessIdRequired;
		},

		productsInBasket() {
			return this.$store.getters['shopapi/getMappedProductsInBasket'];
		},
		cartId() {
			return this.$store.getters.getCartId;
		},

		upgradeOrderId() {
			return this.$store.getters.getUpgradeOrderId;
		},

		accessId() {
			return this.$store.getters.getAccessId;
		},

		preregistrationOverlayTitle() {
			return this.$t('page.shop.experience_prebuilder.preregistration.title', {
				festival: this.shopConfig.festivalData.title
			});
		},

		preregistrationOverlayCopy() {
			return this.$t('page.shop.experience_prebuilder.preregistration.lead', {
				festival: this.shopConfig.festivalData.title
			});
		},

		addonsaleOverlayTitle() {
			return this.$t('page.shop_addon.experience_prebuilder.title', {
				event: this.shopConfig.festivalData.title
			});
		},

		addonsaleOverlayCopy() {
			return this.$t('page.shop_addon.experience_prebuilder.lead');
		},

		isAddonShop() {
			return this.shopConfig && this.shopConfig.shopInit && this.shopConfig.shopInit.isAddOnShop;
			// return this.shopConfig.shopInit && true
		},

		defaultOrderReference() {
			if (this.userData) {
				const { email } = this.userData.profile;
				const references = {
					consumer_email: email || false
				};
				return references;
			} else {
				return false;
			}
		},
		getPaymentProvider() {
			return this.$store.getters['shopconfig/getPaymentProvider'];
		}
	},

	methods: {
		createCart() {
			/*
			 * Create a new cart in the API, the paylogic token is used to create a valid cart
			 */

			const cartId = this.$store.getters['shopconfig/getCartId'] || this.$route.query.cartId || null;
			const cartToken = this.$store.getters['shopconfig/getShopToken'] || this.$route.query.cartToken || null;
			const policyId = this.$store.getters['shopconfig/getPolicyId'] || this.$route.query.policyId || null;
			if (cartId && cartToken) {
				this.$store.commit('shopconfig/setShopToken', cartToken);
				this.$store.commit('setCartId', cartId);

				localStorage.setItem('q_cart_id', cartId);
				localStorage.setItem('q_token', cartToken);

				/*
				 * If the user has done passwordless login the cartId should be in the qeury string
				 * the cart will be then be fetched and set as the default cart.
				 */
				const payload = {
					url: `/carts/${cartId}`,
					config: {
						headers: {
							Authorization: 'Bearer ' + cartToken,
							'X-Q-Dance-Event-Namespace': this.data.apiNamespace
						}
					}
				};

				this.$store
					.dispatch('shopapi/getRequest', payload)
					.then(response => {
						// console.log('cart response', response.data.data);
						this.$store.commit('shopconfig/setData', {
							...this.shopConfig,
							orderServiceFee:
								response.data.data.totalServiceCosts -
									(response.data.data.totalProductsServiceCosts +
										response.data.data.totalEntertainmentFee) || 0
						});
						this.$store.commit('shopconfig/setCart', response.data.data);
						this.$store.commit('setReadytoShop', true);
						this.allowedToRender = true;

						//if the cart is confirmed redirect to checkout
						if (response.data.data.status === 'confirmed') {
							let query = {
								cart: cartId,
								token: cartToken,
								provider: this.getPaymentProvider
							};
							this.$router.push({
								name: 'slug-checkout',
								query: query
							});
							return;
						}

						if (response?.data?.data?.policyId) {
							this.$store.commit('shopconfig/setPolicyId', response.data.data.policyId);
							this.$store
								.dispatch(`getShopproducts`, {
									namespace: this.shopConfig.apiNamespace,
									cartToken: this.shopConfig.shopToken
								})
								.then(response => {
									// cart id meesturen
									this.$store.commit('shopapi/setProducts', response.data.data);
								});
						}
					})
					.catch(error => {
						console.log('error getting cart from query: ', error);
					});

				return;
			}

			// No cart? Create one
			if (
				!this.shopConfig.preregistration &&
				this.$store.getters['shopconfig/getPaylogictoken'] &&
				this.shopCart === null
			) {
				// const this.shopConfig = this.$store.getters['shopconfig/getData'];
				const payload = {
					namespace: this.data.apiNamespace,
					paymentQueueEnabled:
						this.shopConfig.shopInit.paymentQueueEnabled && this.$route.query?.token?.length > 12,
					paymentQueueToken: this.$route.query.token
				};

				if (policyId) payload.policyId = policyId;

				this.$store
					.dispatch('createShopcart', payload)
					.then(response => {
						this.$nextTick(() => {
							localStorage.clear();
							localStorage.setItem('q_token', response.data.data.token);
							localStorage.setItem('q_cart_id', response.data.data.cart.id);
							this.$store.commit('shopconfig/setData', {
								...this.shopConfig,
								shopToken: response.data.data.token,
								orderServiceFee:
									response.data.data.cart.totalServiceCosts -
										(response.data.data.cart.totalProductsServiceCosts +
											response.data.data.cart.totalEntertainmentFee) || 0, //
								//  Store it now for the cart overview, because the api later adds the servicecosts for the selected products.

								upgradableOrder: response.data.data.cart.upgradableOrder || null
							});

							if (policyId) {
								this.$store
									.dispatch(`getShopproducts`, {
										namespace: this.shopConfig.apiNamespace,
										cartToken: this.shopConfig.shopToken
									})
									.then(response => {
										// cart id meesturen
										this.$store.commit('shopapi/setProducts', response.data.data);
									});
							}
							this.$store.commit('shopconfig/setCart', response.data.data.cart);

							// Navigate to first route
							if (this.hasUpgradeModule) {
								this.$router.push(`/${this.$route.params.slug}/upgrade`);
							} else if (
								this.shopConfig.noCookieNeeded &&
								this.forceBackToFlow &&
								this.data.stepflow === 'Yes' &&
								!Object.entries(this.productsInBasket).length
							) {
								if (this.isAddonShop) {
									// console.log('runningAddon')
									this.runAddonShop(response.data.data.token);
								}

								/*
								 * Redirect user to first product route if 'noCookieNeeded'(=if skip prebuilder) and cart contains no products
								 */
								const option =
									this.shopConfig.salesOptions[0].option === 'ticket'
										? 'tickets'
										: this.shopConfig.salesOptions[0].option;

								if (this.$cookies.get('shop_deeplink')) {
									const deeplink = this.$cookies.get('shop_deeplink');

									this.$router.push({
										path: `/${this.$route.params.slug}/${deeplink.path}`,
										query: this.$route.query,
										hash: deeplink.category
									});
									this.$nextTick(() => {
										this.$cookies.remove('shop_deeplink');
									});
								} else {
									this.$router.push({
										path: `/${this.$route.params.slug}/${option}`,
										query: this.$route.query
									});
								}

								this.allowedToRender = true;

								return;
							}
							if (this.$cookies.get('shop_deeplink')) {
								const deeplink = this.$cookies.get('shop_deeplink');

								this.$router.push({
									path: `/${this.$route.params.slug}/${deeplink.path}`,
									query: this.$route.query,
									hash: deeplink.category
								});

								this.$nextTick(() => {
									this.$cookies.remove('shop_deeplink');
								});
							}
							if (this.isAddonShop) {
								this.runAddonShop(response.data.data.token);
							}

							/*
							 * Allow to render the page if a product is in the basket
							 */
							this.allowedToRender = true;
						});
					})
					.catch(error => {
						if (
							error &&
							error.response &&
							error.response.data.error.code &&
							(error.response.data.error.code === 'invalid.upgrade.id' ||
								error.response.data.error.code.startsWith('access_link_in_use'))
						) {
							/*
							 * If shop has an upgrademodule, then with a valid orderid, creating a cart should return an upgradableOffer.
							 * If not, an incorrect order id (or already used order id) was used. So remove user from the upgrade shop.
							 */
							if (this.hasUpgradeModule) {
								this.$fireErrorEventWithPromis(4003);
								this.$router.push({ path: '/', query: this.$route.query });
								return;
							}

							/*
							 * The same error is returned if this shop requires an order id. The error is the same but there is no upgradable order.
							 * Here it's just a way of allowing a selected group of people to enter the shop.
							 */
							if (this.isOrderIdRequired) {
								this.$fireErrorEventWithPromis(4005);
								this.$router.push({ path: '/', query: this.$route.query });
								return;
							}

							if (this.isAccessIdRequired) {
								const accessErrorType = error.response.data.error.code.startsWith('access_link_in_use')
									? 4364
									: 4006;
								this.$fireErrorEventWithPromis(accessErrorType);
								// If only one shop is active, redirecting to home will start an infinite loop of redirecting
								if (!this.isOnlyOneShopActive) {
									this.$router.push({ path: '/' });
									return;
								}

								return this.$nuxt.error({
									message:
										"Your personal link is already used. If you haven't done an order yet, please wait 10 minutes and click on your email link again."
								});
							}
						}

						if (this.isAccessIdRequired && error?.response?.status == 400 && this.isOnlyOneShopActive) {
							this.$fireErrorEventWithPromis(4006);

							return this.$nuxt.error({
								message:
									"Your personal link is already used. <br />If you haven't done an order yet, <br />please wait 10 minutes and click on your email link again."
							});
						}

						if (this.shopConfig.shopInit.paymentQueueEnabled) {
							let urlQueryParams;
							let deeplinkParams;

							const orderId = this.upgradeOrderId || this.$route.query.idt_oss_order_id || null;
							const accessId = this.accessId || this.$route.query.access_id || null;
							const deeplink = this.$cookies.get('shop_deeplink');

							urlQueryParams = new URLSearchParams(window.location.search);

							if (deeplink) {
								urlQueryParams = new URLSearchParams({
									...deeplinkParams,
									...Object.fromEntries(urlQueryParams)
								});
							}

							if (urlQueryParams.has('token') === false && token !== null)
								urlQueryParams.set('token', token);

							if (urlQueryParams.has('idt_oss_order_id') === false && orderId !== null)
								urlQueryParams.set('idt_oss_order_id', orderId);

							if (urlQueryParams.has('access_id') === false && accessId !== null)
								urlQueryParams.set('access_id', accessId);

							this.$nextTick(() => {
								this.$cookies.remove('shop_deeplink');
							});

							let shopBase = 'shop';

							switch (this.brand) {
								case 'zwartecross':
									shopBase = process.env.ENV === 'uat' ? 'uat-shop' : 'tickets';
									break;
								default:
									shopBase = process.env.ENV === 'uat' ? 'uat-shop' : 'shop';
									break;
							}

							const redirectUri =
								window.location.hostname !== 'localhost' && !this.shopConfig.queueUrl
									? `https://${shopBase}.${this.ossConfig.shopDomain}/${this.$route.params.slug}`
									: this.shopConfig.queueUrl;

							if (this.shopConfig.queueUrl) urlQueryParams.delete('token');

							window.location.replace(preserveUTMParams(`${redirectUri}?${urlQueryParams.toString()}`));

							return false;
						}
					});
			}

			if (this.shopConfig.preregistration) {
				let urlQueryParams;
				let deeplinkParams;

				if (
					(!this.$route.query.token || this.$route.query.token.length < 10) &&
					this.shopConfig.queueUrl &&
					window.location.hostname !== 'localhost'
				) {
					const orderId = this.upgradeOrderId || this.$route.query.idt_oss_order_id || null;
					const accessId = this.accessId || this.$route.query.access_id || null;
					const deeplink = this.$cookies.get('shop_deeplink');

					urlQueryParams = new URLSearchParams(window.location.search);

					if (deeplink) {
						urlQueryParams = new URLSearchParams({
							...deeplinkParams,
							...Object.fromEntries(urlQueryParams)
						});
					}

					if (urlQueryParams.has('idt_oss_order_id') === false && orderId !== null)
						urlQueryParams.set('idt_oss_order_id', orderId);

					if (urlQueryParams.has('access_id') === false && accessId !== null)
						urlQueryParams.set('access_id', accessId);

					const redirectUri = new URL(this.shopConfig.queueUrl);

					if (this.shopConfig.queueUrl) urlQueryParams.delete('token');

					window.location.replace(preserveUTMParams(`${redirectUri}?${urlQueryParams.toString()}`));

					this.$nextTick(() => {
						this.$cookies.remove('shop_deeplink');
					});
				} else {
					const fakeCart = {
						id: 0,
						status: 'preorder',
						products: {},
						totalAmountExcludingServiceCosts: 0,
						totalServiceCosts: 0,
						totalAmount: 0,
						timeout: 1200,
						order: null
					};

					this.$store.commit('shopconfig/setCart', fakeCart);
					this.$store.commit('setReadytoShop', true);
					if (
						/*
						 * Redirect user to first product route if cart contains no products
						 */
						this.forceBackToFlow &&
						this.data.stepflow === 'Yes' &&
						!Object.entries(this.productsInBasket).length
					) {
						// Since we're skipping the overview page, show a message telling the user this is preregistration mode.
						this.$store.commit('setModalTitle', this.preregistrationOverlayTitle); // message that gets shown by 8000 error modal
						this.$store.commit('setModalMessage', this.preregistrationOverlayCopy); // message that gets shown by 8000 error modal

						this.$nextTick(() => {
							this.$fireErrorEvent(8003);
						});

						const option =
							this.shopConfig.salesOptions[0].option === 'ticket'
								? 'tickets'
								: this.shopConfig.salesOptions[0].option;

						if (this.$cookies.get('shop_deeplink')) {
							const deeplink = this.$cookies.get('shop_deeplink');

							this.$router.push({
								path: `/${this.$route.params.slug}/${deeplink.path}`,
								query: this.$route.query,
								hash: deeplink.category
							});

							this.$nextTick(() => {
								this.$cookies.remove('shop_deeplink');
							});
						} else {
							let query = {};
							const seenKeys = new Set();
							for (const key in this.$route.query) {
								if (key.startsWith('utm_') || (key.startsWith('lang') && !seenKeys.has(key))) {
									query[key] = this.$route.query[key];
									seenKeys.add(key);
								}
							}
							this.$router.push({ path: `/${this.$route.params.slug}/${option}`, query: query });
							this.allowedToRender = true;
						}

						return;
					} else {
						this.allowedToRender = true;
					}
				}
			}
		},

		runAddonShop(cartToken) {
			// console.log('Cart for Addon')
			// Since we're skipping the overview page, show a message telling the user this is addon mode.
			this.$store.commit('setModalTitle', this.addonsaleOverlayTitle); // message that gets shown by 8000 error modal
			this.$store.commit('setModalMessage', this.addonsaleOverlayCopy); // message that gets shown by 8000 error modal

			this.$nextTick(() => {
				this.$fireErrorEvent(8003);
			});

			this.$store
				.dispatch(`getOrder`, {
					namespace: this.data.apiNamespace,
					reference: this.orderReference || this.defaultOrderReference,
					cartToken: cartToken
				})
				.then(response => {
					let error = !!response;
					this.$store.commit('shopapi/setPurchasedProducts', response);

					if (!error) {
						this.$store.commit('shopapi/setHasPreviousOrder', !response);
					} else {
						this.$store.commit('shopapi/setHasPreviousOrder', !!response);
					}

					this.$store.commit('setReadytoShop', true);

					this.$store
						.dispatch(`getShopproducts`, { namespace: this.data.apiNamespace, cartToken: cartToken })
						.then(response => {
							// cart id meesturen
							this.$store.commit('shopapi/setProducts', response.data.data);
						});

					if (this.shopConfig.noCookieNeeded) {
						// const option = this.shopConfig.salesOptions[0].option
						const option =
							this.shopConfig.salesOptions[0].option === 'ticket'
								? 'tickets'
								: this.shopConfig.salesOptions[0].option;
						if (
							!response?.data?.data['upsell']?.hospitality['add_on_available'] &&
							option === 'traveladdon'
						) {
							// console.log('toTravel')
							const filteredOptions = this.shopConfig.salesOptions.filter(
								optionOjb => optionOjb.option !== 'traveladdon'
							);
							this.$router.push({
								path: `/${this.$route.params.slug}/${filteredOptions[0].option}`,
								query: this.$route.query
							});
						} else {
							// console.log('toTravelAddon')
							this.$router.push({
								path: `/${this.$route.params.slug}/${option}`,
								query: this.$route.query
							});
						}
					}
				});
		},

		getShopproducts() {
			/*
			 * Get all the shop products and save them to the shopapi store
			 * If they decide to load a static product json to reduce API load these checks are needed
			 */
			if (this.isAddonShop) {
				this.$store
					.dispatch(`getShopproducts`, {
						namespace: this.shopConfig.apiNamespace,
						cartToken: this.shopConfig.shopToken
					})
					.then(response => {
						// cart id meesturen
						this.$store.commit('shopapi/setProducts', response.data.data);
						this.createCart();
					});
				// console.log(this.$store.getters['shopapi/getPurchasedProducts'])
			} else if (!this.shopConfig.preregistration || !this.shopConfig.loadproductsfromjson) {
				this.$store.dispatch(`getShopproducts`, { namespace: this.data.apiNamespace }).then(response => {
					this.$store.commit('shopapi/setProducts', response.data.data);
					this.createCart();
				});
			} else {
				console.log('deprecated productJsonUrl and loadproductsfromjson');
				this.$axios.get(this.shopConfig.productjsonurl).then(response => {
					this.$store.commit('shopapi/setProducts', response.data.data);
					this.createCart();
				});
			}
		},

		initShop() {
			if (this.shopCart === null) {
				/*
				 * If they decide to load a static product json to reduce API load this check is needed
				 */

				if (!this.shopConfig.preregistration || !this.shopConfig.loadproductsfromjson) {
					/*
					 * Load the products from the API
					 */
					this.$store.dispatch(`getShopinit`, this.data.apiNamespace).then(response => {
						this.isShopReady = true;
						if (response.data.data.isAddOnShop) {
							this.$store.commit('setModalPrefillCartActive', true);
						}
						if (response.data.data.isOpen) {
							this.$store.commit('shopconfig/setIsOpen', response.data.data.isOpen);
							const newData = {
								shopInit: response.data.data,
								...this.data
							};

							this.$store.commit('shopconfig/setData', newData);
							this.$store.commit('shopconfig/setPaymentProvider', response.data.data.paymentProvider);

							if (this.$route.query.token || this.$store.getters['shopconfig/getPaylogictoken']) {
								this.getShopproducts();
							} else {
								this.allowedToRender = true;
							}

							return;
						}

						this.$store.commit('shopconfig/setIsOpen', false);

						/*
						 * Show model that the shop is closed
						 */
						this.$fireErrorEvent(4000);
						this.$router.push({ path: '/', query: this.$route.query });
					});
				} else {
					/*
					 * Load the products from a json file. First load the shop init
					 */
					console.log('deprecated shopJsonUrl and loadproductsfromjson');
					this.$axios.get(this.shopConfig.shopjsonurl).then(response => {
						this.isShopReady = true;
						const newData = {
							shopInit: response.data.data,
							...this.data
						};
						this.$store.commit('shopconfig/setData', newData);
						this.getShopproducts();
					});
				}
			} else {
				this.authRequirements = true;
				this.isShopReady = true;
				this.allowedToRender = true;
				this.forceBackToFlow = false;
			}
		},

		initShopWithOrderReference() {
			// console.log(this.orderReference)
			this.initShop();
		}

		/* checkDeeplink() {
			return;
			if (this.shopCart) {
				// const cookieDeeplinkName = this.$cookies.get('deeplink');

				// if (cookieDeeplinkName) {
				// 	this.$cookies.remove('deeplink');
				// 	this.$router.push({
				// 		name: cookieDeeplinkName,
				// 		params: {
				// 			slug: this.$route.params.slug
				// 		}
				// 	});
				if (
					this.forceBackToFlow &&
					this.data.stepflow === 'Yes' &&
					!Object.entries(this.productsInBasket).length
				) {
					const option =
						this.shopConfig.salesOptions[0].option === 'ticket'
							? 'tickets'
							: this.shopConfig.salesOptions[0].option;

					this.$router.push(`${this.$route.params.slug}/${option}`);
				}
			}
		} */
	},

	created() {
		if (!this.data.saleActive) {
			this.$router.push({ path: '/', query: this.$route.query });
			return;
		}

		this.$store.commit('shopconfig/setPaylogictoken', this.$route.query.token);
	},

	mounted() {
		// Document isn't defined earlier than here
		this.setCssVars(false);

		// 	if (!this.$cookies.get('shop_deeplink') && this.$route.query.route) {
		// 		const deeplink = {
		// 			route: this.$route.query.route,
		// 			slug: this.$route.query.slug || null,
		// 			category: this.$route.query.category || null,
		// 			raw: `${this.$route.fullPath.split('?')[1]}`
		// 		};

		// 		this.$cookies.remove('shop_deeplink');

		// 		this.$nextTick(() => {
		// 			this.$cookies.set(`shop_deeplink`, JSON.stringify(deeplink), {
		// 				path: '/',
		// 				maxAge: 6 * 30 * 24 * 3600 // 6 months
		// 			});
		// 		});
		// 	}
	}
};
</script>

<style lang="scss">
$component: 'experiance-builder';

.#{$component} {
	display: block;
	color: var(--detail-text-color);

	&__fake-space {
		min-height: 50vh;
	}
}
</style>
